import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { formEventModule } from "form/event"
import { refreshInventory } from "form/inventory/tools/common"
import {
  FORM_EVENTS,
  IForm,
  IFormDataLoader,
  IFormEnvironmentTreeInfo,
  SELECT_FILTER_VALUES,
  USER_PREF_ENVIRONMENT_IS_TREE_VIEW
} from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getForm } from "form/tools/common"
import { messageSVC } from "message/service"
import { normalizeSync } from "normalize-diacritics"
import { userSVC } from "user/service"
import { getUserPreference } from "user/tools/preference"
import { IEnvironment, IEnvironmentsSetFilterParams, OTHER_ENVIRONMENT_GROUP_ID } from "../../environments/model"
import { environmentsRPO } from "../../environments/repository"
import { getEnvironmentById, getEnvironmentList, getEnvironmentTree } from "../../environments/tools/common"
import { getScreensChanges } from "./screens"

export const environmentsFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const envs = await environmentsRPO.getCampaignEnvironments(form.tempCampaignId)
    form.context.relations.environments = envs.length === 0 ? null : envs
    form.screens.environmentsIds = envs.map(e => e.venue_type_id)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Environment data-loader error ${String(error)}`)
  }
}

export function synchronizeNewEnvironmentsSelection(newSelectedEnvironmentIds: number[]): void {
  getStore().dispatch(formActionCreator.startSynchronizingEnvironments(getForm().context.appContext))
  clearTimeout(state.environmentsTimeout)
  state.environmentsTimeout = setTimeout(() => setEnvironments(newSelectedEnvironmentIds), 800)
}

export function setEnvironmentsFilters(params: IEnvironmentsSetFilterParams): void {
  getStore().dispatch(formActionCreator.setEnvironmentsFilter(getForm().context.appContext, params))
}

export function getFilteredEnvironments(
  nameFilter: string,
  selectFilter: SELECT_FILTER_VALUES,
  selectedIds: number[]
): IEnvironment[] {
  const allEnviromnments = getEnvironmentList()
  if (!nameFilter && selectFilter === SELECT_FILTER_VALUES.ALL) {
    return allEnviromnments
  }
  const filteredEnvironments: IEnvironment[] = []
  const preparedNameFilter = normalizeSync(nameFilter).toLowerCase()
  for (const environment of allEnviromnments) {
    if (nameFilter && normalizeSync(environment.name).toLowerCase().indexOf(preparedNameFilter) === -1) {
      continue
    }
    if (selectFilter !== SELECT_FILTER_VALUES.ALL) {
      const isSelected = selectedIds.includes(environment.id)
      if (selectFilter === SELECT_FILTER_VALUES.SELECTED) {
        if (!isSelected) {
          continue
        }
      } else if (isSelected) {
        continue
      }
    }
    filteredEnvironments.push(environment)
  }
  return filteredEnvironments
}

export function noticeAndClearEnvironmentsError(form: IForm): void {
  messageSVC.error(translate("environments.error.selection"))
  getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { environmentsError: false }))
}

export function toggleEnvironmentTreeElement(form: IForm, environmentId: number): void {
  const expandedIds = form.screens.environmentExpandedIds
  let newExpandedIds: number[]
  if (expandedIds.includes(environmentId)) {
    newExpandedIds = expandedIds.filter(id => id !== environmentId)
  } else {
    newExpandedIds = [...expandedIds, environmentId]
  }
  getStore().dispatch(formActionCreator.setEnvironmentsExpandedIds(form.context.appContext, newExpandedIds))
}

export function setEnvironmentIsTreeView(form: IForm, isTreeView: boolean): void {
  userSVC.setPreference(USER_PREF_ENVIRONMENT_IS_TREE_VIEW, String(isTreeView))
  getStore().dispatch(formActionCreator.setEnvironmentIsTreeView(isTreeView))
}

export function notifyEnvironmentsSelectionChanged(newSelection: number[]): void {
  formEventModule.notify(FORM_EVENTS.ENVIRONMENTS_SELECTION_CHANGED, newSelection)
}

// @recursive
export function getEnvironmentTreeInfo(
  expandedIds: number[],
  selectedIds: number[],
  environmentId: number
): IFormEnvironmentTreeInfo {
  if (environmentId === OTHER_ENVIRONMENT_GROUP_ID) {
    return {
      isSelected: false,
      hasOneParentSelected: false,
      isTargeted: selectedIds.length === 0,
      isDisabled: true
    }
  }
  const areAllUnselected = selectedIds.length === 0
  const environment = getEnvironmentById(environmentId)
  const isSelected = selectedIds.contains(environmentId)
  if (!environment.parent) {
    return {
      isSelected,
      hasOneParentSelected: false,
      isTargeted: areAllUnselected || isSelected,
      isDisabled: false
    }
  }
  // @recursivity
  const parentInfo = getEnvironmentTreeInfo(expandedIds, selectedIds, environment.parent.id)
  const hasOneParentSelected = parentInfo.isSelected || parentInfo.hasOneParentSelected
  return {
    isSelected,
    hasOneParentSelected,
    isTargeted: areAllUnselected || isSelected || hasOneParentSelected || parentInfo.isTargeted,
    isDisabled: !areAllUnselected && !isSelected && hasOneParentSelected
  }
}

// @recursive
export function isEnvironmentVisible(expandedIds: number[], environment: IEnvironment): boolean {
  if (!environment.parent) {
    return true
  }
  return expandedIds.includes(environment.parent.id)
}

export function checkEnvironmentIsTreeViewPreference(): void {
  const pref = getUserPreference(USER_PREF_ENVIRONMENT_IS_TREE_VIEW)
  const isTreeView = pref !== "false"
  if (isTreeView !== getStore().getState().form.isEnvironmentTreeView) {
    getStore().dispatch(formActionCreator.setEnvironmentIsTreeView(isTreeView))
  }
}

export function getEnvironmentsTargetedCount(form: IForm): number {
  const selectedIds = form.screens.environmentsIds
  if (selectedIds.length === 0) {
    return 0
  }
  const tree = getEnvironmentTree()
  let result = 0
  for (const treeElement of tree) {
    if (treeElement.id !== OTHER_ENVIRONMENT_GROUP_ID && selectedIds.includes(treeElement.id)) {
      result += 1 + getTreeElementChildCount(treeElement)
    } else {
      result += getEnvironmentChildrenTargetedCount(selectedIds, treeElement)
    }
  }
  return result
}

// @recursive
export function getEnvironmentChildrenTargetedCount(selectedIds: number[], treeElement: IEnvironment): number {
  let result: number = 0
  for (const child of treeElement.children) {
    if (selectedIds.includes(child.id)) {
      result += 1 + getTreeElementChildCount(child)
    } else {
      // @recursivity
      result += getEnvironmentChildrenTargetedCount(selectedIds, child)
    }
  }
  return result
}

// @recursive
function getTreeElementChildCount(treeElement: IEnvironment): number {
  if (treeElement.children.length === 0) {
    return 0
  }
  let result = treeElement.children.length
  for (const child of treeElement.children) {
    if (child.children.length !== 0) {
      // @recursivity
      result += getTreeElementChildCount(child)
    }
  }
  return result
}

// @recursive
async function setEnvironments(newSelectedEnvironmentIds: number[]): Promise<void> {
  if (state.environmentsIsSynchronizing) {
    state.environmentsToApplySelection = newSelectedEnvironmentIds
    return
  } else {
    state.environmentsIsSynchronizing = true
  }
  const form = getForm()
  let hasError = false
  try {
    const newEnvironmentsAssociations = await environmentsRPO.setEnvironments(
      form.tempCampaignId,
      newSelectedEnvironmentIds
    )
    getStore().dispatch(
      formActionCreator.setFormEnvironments(
        form.context.appContext,
        getScreensChanges({
          ...form,
          screens: {
            ...form.screens,
            environmentsIds: newSelectedEnvironmentIds
          }
        }),
        newEnvironmentsAssociations
      )
    )
    notifyEnvironmentsSelectionChanged(newSelectedEnvironmentIds)
  } catch (error) {
    console.error(error)
    hasError = true
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { environmentsError: hasError }))
    getStore().dispatch(formActionCreator.stopSynchronizingEnvironments(getForm().context.appContext))
  }
  state.environmentsIsSynchronizing = false
  if (state.environmentsToApplySelection) {
    const toApplyEnvironmentsSelection = state.environmentsToApplySelection
    state.environmentsToApplySelection = null
    // @recursivity
    setEnvironments(toApplyEnvironmentsSelection)
  } else if (!hasError) {
    refreshInventory()
  }
}
